import store from '@/store/index';
import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action,
} from 'vuex-module-decorators';
import {
  Invoicing,
  InvoicingData,
  InvoicingFilter,
} from './invoicing-types';
import {
  defaultFilter,
  createInvoicingData
} from './invoicing-factory';

import InvoicingService from '@/services-http/servicos/InvoicingService';
import Formatters from '@/shared/formatters/formatters';

const formatter = new Formatters();

@Module({
  namespaced: true,
  name: 'invoicing',
  dynamic: true,
  store,
})
class InvoicingStore extends VuexModule {
  data: InvoicingData = createInvoicingData();

  // Getters

  get automaticSearchFields() {
    return [
      'competenceMonth',
      'competenceYear',
      'name',
      'cpf',
      'page',
      'size',
    ];
  }

  get selectedInvoices() {
    return this.data.selectedInvoices;
  }

  get showDownloadHistoryModal() {
    return this.data.showDownloadHistoryModal;
  }

  get loadPage() {
    return this.data.loadingPage;
  }

  get invoices() {
    return this.data.invoices;
  }

  get invoicingFilter() {
    return this.data.filter;
  }

  get pageMounted() {
    return this.data.pageMounted;
  }

  get invoicesTotalPages() {
    return this.data.totalPages;
  }

  get invoicesTotalElements() {
    return this.data.totalElements;
  }

  get invoicingFilterCounter() {
    const {
      competencies,
      carrierIds,
      contractIds,
      benefitIds,
      financialGroupIds,
      invoiceTypeIds,
      subContractIds,
      page,
      size,
      sort,
      ...filter
    } = this.data.filter;

    return Object.values(filter).filter(value =>
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim() === '') &&
      !(Array.isArray(value) && value.length === 0)
    ).length;
  }
  get showRequestModal() {
    return this.data.showExportRequestModal;
  }

  get showConfirmModal() {
    return this.data.showExportConfirmModal;
  }

  get filterWithFilledFields() {
    return formatter.getFilledKeysFromObject(this.invoicingFilter);
  }

  get exportFilterFields() {
    const allowedFields = [
      'competencies',
      'carrierIds',
      'contractIds',
      'benefitIds',
      'financialGroupIds',
      'invoiceTypeIds',
      'subContractIds',
    ];

    return Object.fromEntries(
      Object.entries(this.filterWithFilledFields)
        .filter(([key]) => allowedFields.includes(key))
    );
  }
  get invoicingTab() {
    return this.data.invoicingTab;
  }

  // Actions
  @Action
  exportReport() {
    const invoicingService = new InvoicingService();
    return invoicingService.Export(this.exportFilterFields);
  }

  @Action
  async fetchInvoices() {
    try {
      const paramString = Object.entries(this.filterWithFilledFields)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
      const invoicingService = new InvoicingService();
      const result = await invoicingService.FetchInvoices(paramString);

      const invoices = result.data.content as Invoicing[];

      this.SET_INVOICES(invoices);
      this.SET_TOTAL_PAGES(result.data.totalPages);
      this.SET_TOTAL_ELEMENTS(result.data.totalElements);
    } catch (error) {
      return error;
    }
  }

  @Action
  async searchInvoices() {
    try {
      if (this.data.pageMounted) this.loadingPage(true);

      await this.fetchInvoices();

      if (this.data.pageMounted) this.loadingPage(false);
    } catch (error) {
      return error;
    }
  }

  @Action
  async resetFilter(isRhprotegido: boolean) {
    this.RESET_FILTER(isRhprotegido);

    await this.searchInvoices();
  }

  @Action
  async setInvoicingFilter(filter: Partial<InvoicingFilter>) {
    this.SET_FILTER(filter);

    if (this.automaticSearchFields.some((key) => key in filter)) {
      await this.searchInvoices();
    }
  }

  // Mutations

  @Mutation
  setShowDownloadHistoryModal(show: boolean) {
    this.data.showDownloadHistoryModal = show;
  }

  @Mutation
  showExportRequestModal(show: boolean) {
    this.data.showExportRequestModal = show;
  }

  @Mutation
  showExportConfirmModal(show: boolean) {
    this.data.showExportConfirmModal = show;
  }

  @Mutation
  setInvoicingTab(tab: 'invoicing' | 'reports') {
    this.data.invoicingTab = tab;
  }

  @Mutation
  setPageMounted(mounted: boolean) {
    this.data.pageMounted = mounted;
  }

  @Mutation
  loadingPage(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  resetState() {
    this.data = createInvoicingData();
  }

  //  Private Mutations
  @Mutation
  private RESET_FILTER(isRhprotegido: boolean) {
    const filterEmpty = defaultFilter();
    const filter = {
      ...filterEmpty,
      financialGroupIds: isRhprotegido ? this.data.filter.financialGroupIds : null,
    }

    this.data.filter = filter;
  }

  @Mutation
  private SET_FILTER(filter: Partial<InvoicingFilter>) {
    this.data.filter = {
      ...this.data.filter,
      ...filter,
    }
  }

  @Mutation
  private SET_INVOICES(invoices: Invoicing[]) {
    this.data.invoices = invoices;
  }

  @Mutation
  private SET_TOTAL_PAGES(totalPages: number) {
    this.data.totalPages = totalPages;
  }

  @Mutation
  private SET_TOTAL_ELEMENTS(totalElements: number) {
    this.data.totalElements = totalElements;
  }
}

export default getModule(InvoicingStore);